import { useEffect, useRef, useState } from 'react';
import type { PrintArea } from '@/lib/prize-product';
import {
  type Box, type Nat, type ImageLoadState,
  clampBox, nudgeBox, scaleFor, toNative, toScreen, zoneBoxLabel, zoneColor, MIN_ZONE,
} from '@/lib/prize-zones';

// Multi-zone placement canvas for ONE garment view of a prize product.
// Native port of product-config-admin/src/App.jsx MockupEditor, kept
// behaviour-identical to the console's team-store ViewZoneCanvas (move +
// SE-corner resize + draw-to-place, pointer events, no drag library).
//
// Boxes are drawn from, and written back in, the mockup image's NATIVE pixels —
// the same numbers proof-email-function composites against.
//
// The image has THREE states, not one (Codex F10). onLoad alone meant a base
// image that 404s sat on "waiting to load" forever with no way to tell it from a
// slow one, and no way to retry. A failure is now named, retryable, and reported
// upward so the editor can block the save.
//
// Zone boxes and resize handles are real keyboard controls (Codex F11): they
// were pointer-only <div>s, which put the print position of every prize product
// out of reach of anyone who cannot drag a mouse.
type Props = {
  baseImage: string;
  overlayImage?: string;
  /** Every print area on the product — indexes the colour palette by code. */
  allCodes: string[];
  /** Areas placed on THIS view, in draw order. */
  areas: Record<string, PrintArea>;
  codes: string[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
  onChangeBox: (code: string, box: Box) => void;
  onNatural: (nat: Nat) => void;
  /** Load state of the BASE image, so the editor can gate on a failure. */
  onLoadState?: (state: ImageLoadState) => void;
};

const mockupSrc = (file: string, attempt: number) => {
  const path = `/api/prize-products/mockups/${encodeURIComponent(file)}`;
  // A retry must actually re-request: without a changing query the browser
  // serves the cached failure and the button looks broken.
  return attempt > 0 ? `${path}?retry=${attempt}` : path;
};

const boxOf = (area: PrintArea): Box => ({
  x: area.mockup?.x ?? 0,
  y: area.mockup?.y ?? 0,
  maxW: area.mockup?.maxW ?? MIN_ZONE,
  maxH: area.mockup?.maxH ?? MIN_ZONE,
});

export function PrizeZoneCanvas({
  baseImage, overlayImage, allCodes, areas, codes, selectedCode, onSelect, onChangeBox, onNatural,
  onLoadState,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [nat, setNat] = useState<Nat | null>(null);
  const [dispW, setDispW] = useState(0);
  const [status, setStatus] = useState<ImageLoadState>('loading');
  const [attempt, setAttempt] = useState(0);
  const drag = useRef<null | { mode: 'move' | 'resize' | 'draw'; code: string; startX: number; startY: number; orig: Box }>(null);

  const scale = scaleFor(nat, dispW);

  // A new file is a new measurement: drop the old natural size and go back to
  // 'loading' so a stale success cannot mask a broken replacement.
  useEffect(() => {
    setNat(null);
    setDispW(0);
    setAttempt(0);
    setStatus('loading');
    onLoadState?.('loading');
    // onLoadState is a render-scoped callback; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseImage]);

  function settle(next: ImageLoadState) {
    setStatus(next);
    onLoadState?.(next);
  }

  function startDrag(mode: 'move' | 'resize', code: string, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const area = areas[code];
    if (!area) return;
    onSelect(code);
    drag.current = { mode, code, startX: e.clientX, startY: e.clientY, orig: boxOf(area) };
    wrapRef.current?.setPointerCapture(e.pointerId);
  }

  // Dragging on bare image re-draws the SELECTED zone's box (PCA: "select an
  // area, then draw on the image"). Never creates a zone — codes come from the
  // registry picker, so a stray drag can't invent a print area. Nothing is
  // written on pointer-DOWN: a plain click must not collapse a placed zone to
  // the minimum box (PCA only commits its tempRect once the pointer moves).
  function startDraw(e: React.PointerEvent) {
    if (!nat || !selectedCode || !areas[selectedCode] || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const anchor = clampBox(
      { x: toNative(e.clientX - rect.left, scale), y: toNative(e.clientY - rect.top, scale), maxW: MIN_ZONE, maxH: MIN_ZONE },
      nat,
    );
    drag.current = { mode: 'draw', code: selectedCode, startX: e.clientX, startY: e.clientY, orig: anchor };
    wrapRef.current.setPointerCapture(e.pointerId);
  }

  function onMove(e: React.PointerEvent) {
    const d = drag.current;
    if (!d || !nat) return;
    const dx = toNative(e.clientX - d.startX, scale);
    const dy = toNative(e.clientY - d.startY, scale);
    const next: Box = d.mode === 'move'
      ? { ...d.orig, x: d.orig.x + dx, y: d.orig.y + dy }
      : { ...d.orig, maxW: d.orig.maxW + dx, maxH: d.orig.maxH + dy }; // resize + draw share the grow math
    onChangeBox(d.code, clampBox(next, nat));
  }

  const endDrag = () => { drag.current = null; };

  // Arrow keys move, Shift+arrows resize, Alt takes a 10px step. `resizeOnly` is
  // the handle: it is its own tab stop and its arrows resize without a modifier.
  function onZoneKey(code: string, e: React.KeyboardEvent, resizeOnly = false) {
    const area = areas[code];
    if (!area || !nat) return;
    const next = nudgeBox(boxOf(area), nat, e.key, {
      shift: resizeOnly || e.shiftKey,
      alt: e.altKey,
    });
    if (!next) return;                 // not an arrow — let Tab/Enter through
    e.preventDefault();
    e.stopPropagation();
    onSelect(code);
    onChangeBox(code, next);
  }

  const failed = status === 'failed';

  return (
    <div className="min-w-0">
      <div
        ref={wrapRef}
        className="relative inline-block touch-none select-none overflow-hidden rounded-[10px] border border-black/[0.07] bg-[conic-gradient(at_25%_25%,#fafafa_90deg,#f1f1f3_90deg_180deg,#fafafa_180deg_270deg,#f1f1f3_270deg)] bg-[length:20px_20px]"
        onPointerDown={startDraw}
        onPointerMove={onMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {/* The failed image is NOT rendered as a broken <img>: a broken-image
            glyph reads as "still loading" to most people. */}
        {failed ? (
          <div
            role="alert"
            className="flex h-[260px] w-[320px] max-w-full flex-col items-center justify-center gap-2 px-4 text-center"
          >
            <p className="text-[12px] font-medium text-red-700">This mockup image could not be loaded.</p>
            <p className="text-[11px] text-muted">
              <code className="text-[10.5px]">{baseImage}</code> did not come back from the mockup
              bucket. Zone positions are measured in this image's pixels, so nothing can be placed
              or saved against it until it loads.
            </p>
            <button
              type="button"
              onClick={() => { setAttempt((a) => a + 1); setStatus('loading'); onLoadState?.('loading'); }}
              className="rounded-[8px] border border-black/[0.12] bg-surface px-2.5 py-1 text-[11px] font-medium hover:bg-bg"
            >
              Retry loading
            </button>
            <p className="text-[10px] text-subtle">…or pick a different file / upload a replacement below.</p>
          </div>
        ) : (
          <img
            key={`${baseImage}#${attempt}`}
            src={mockupSrc(baseImage, attempt)}
            alt="garment mockup"
            draggable={false}
            className="block max-h-[460px] max-w-full"
            onLoad={(e) => {
              const n = { w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight };
              // A 200 that decodes to a 0×0 image is a failure, not a load: it
              // would hand the editor a canvas it cannot lay anything out in.
              if (!(n.w > 0) || !(n.h > 0)) { settle('failed'); return; }
              setNat(n);
              setDispW(e.currentTarget.clientWidth);
              onNatural(n);
              settle('loaded');
            }}
            onError={() => settle('failed')}
          />
        )}

        {/* Distinct from failed: this one resolves on its own, so it says wait. */}
        {status === 'loading' && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-white/55"
            role="status"
          >
            <span className="rounded-[8px] bg-surface px-2 py-1 text-[11px] text-muted shadow-sm">
              Loading {baseImage}…
            </span>
          </div>
        )}

        {overlayImage && !failed && (
          <img
            src={mockupSrc(overlayImage, attempt)}
            alt=""
            draggable={false}
            className="pointer-events-none absolute inset-0 h-full w-full"
          />
        )}

        {nat && !failed && codes.map((code) => {
          const area = areas[code];
          if (!area?.mockup) return null;
          const b = boxOf(area);
          const color = zoneColor(allCodes, code);
          const selected = code === selectedCode;
          const inches = area.widthInches && area.heightInches ? `${area.widthInches}×${area.heightInches} in` : 'size not set';
          return (
            <div
              key={code}
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              aria-label={zoneBoxLabel(code, b, nat)}
              className="absolute cursor-move focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/40"
              style={{
                left: toScreen(b.x, scale),
                top: toScreen(b.y, scale),
                width: toScreen(b.maxW, scale),
                height: toScreen(b.maxH, scale),
                border: `2px solid ${color}`,
                background: `${color}${selected ? '2e' : '1a'}`,
                boxShadow: selected ? `0 0 0 2px ${color}55` : undefined,
                borderRadius: 4,
                zIndex: selected ? 3 : 1,
              }}
              onPointerDown={(e) => startDrag('move', code, e)}
              onFocus={() => onSelect(code)}
              onKeyDown={(e) => onZoneKey(code, e)}
            >
              <span
                className="absolute -top-5 left-0 whitespace-nowrap rounded-[4px] px-1.5 py-px text-[9.5px] font-semibold text-white"
                style={{ background: color }}
                aria-hidden
              >
                {code} · {inches}
              </span>
              <div
                role="button"
                tabIndex={0}
                aria-label={`Resize ${code}. Arrow keys change the width and height.`}
                className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 cursor-se-resize rounded-[4px] border-2 border-white focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/40"
                style={{ background: color }}
                onPointerDown={(e) => startDrag('resize', code, e)}
                onKeyDown={(e) => onZoneKey(code, e, true)}
              />
            </div>
          );
        })}
      </div>

      {codes.length > 0 && !failed && (
        <p className="mt-1 text-[10px] text-subtle">
          Tab to a zone, then arrow keys to move it, Shift + arrows to resize, Alt for 10px steps.
          Exact numbers can be typed in the zone panel.
        </p>
      )}
    </div>
  );
}
